import { RadioTower } from 'lucide-react'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import type { Standings } from '@/lib/watch/broadcastData'
import type { EventAthlete } from '@/lib/watch/eventAthletes'
import { PANEL_LIMITS, type RaceGender, type StandingsGraphic } from '@/lib/watch/graphics'
import { raceById, standingsTitle, type Race } from '@/lib/watch/races'
import { useServerNow } from '@/lib/watch/useServerNow'
import { useStandings } from '@/lib/watch/useStandings'
import type { BroadcastElementProps } from './context'
import { GENDER_GLYPH } from './glyphs'
import { BroadcastPanel, BroadcastRow, RaceTag, shortAge } from './primitives'
import './broadcast.css'

/** A board's rows when its spec doesn't say: a quiet five, or everyone a hand-picked board names (up to ten). */
export const DEFAULT_ROWS = 5
/** Upright on a phone, a board shows its top five. */
export const COMPACT_ROWS = 5

/** How many rows a board asks for. */
export function rowsFor(spec: Pick<StandingsGraphic, 'limit' | 'athleteIds'>): number {
  if (spec.limit) return spec.limit
  return spec.athleteIds ? Math.min(Math.max(spec.athleteIds.length, PANEL_LIMITS.min), PANEL_LIMITS.max) : DEFAULT_ROWS
}

export interface StandingsPanelViewProps {
  standings: Standings
  race: Race
  /** Whom it ranks: Venus or Mars beside the race in its title, said "Women · 10 km"; null ranks everyone. */
  gender: RaceGender | null
  /** The host's or Badger's own title, shown in words in place of the glyph and race; null for none. */
  ownTitle?: string | null
  /** Photos by athlete id (the roster the page holds); a row without one shows initials. */
  roster: ReadonlyMap<string, EventAthlete>
  /** At most this many rows. */
  limit: number
  now: number
  compact?: boolean
  preview?: boolean
}

/**
 * A leaderboard in the panel (docs/features/badger-cohost.md, Elements), icons over words: its title is Venus or Mars
 * beside the race ("♀ ● 10 km", said "Women · 10 km"), or the host's own words; then rank, photo, name, kilometres and
 * the gap to the first per row; under them the tracker glyph and how fresh ("12s", said "From live trackers, updated 12s
 * ago"): tracker standings are never results. Stale rows dim and carry the clock; finishers carry the flag. Upright on a
 * phone it shows the top five.
 */
export function StandingsPanelView({ standings, race, gender, ownTitle = null, roster, limit, now, compact = false, preview = false }: StandingsPanelViewProps) {
  const rows = standings.rows.slice(0, compact ? Math.min(limit, COMPACT_ROWS) : limit)
  const own = ownTitle?.trim() || null
  const Glyph = gender ? GENDER_GLYPH[gender] : null
  const title = own ?? (
    <>
      <span className="bc-panel__mark" aria-hidden="true">
        {Glyph && <Glyph className="bc-panel__glyph" />}
        <RaceTag race={race} />
      </span>
      <span className="sr-only">{standingsTitle(race, gender)}</span>
    </>
  )
  const ms = now - standings.at
  return (
    <BroadcastPanel
      title={title}
      aside={own ? <RaceTag race={race} /> : null}
      preview={preview}
      compact={compact}
      footnote={
        <>
          <RadioTower aria-hidden="true" className="bc-panel__foot-glyph" />
          <span aria-hidden="true">{shortAge(ms)}</span>
          <span className="sr-only">From live trackers, updated {formatAge(ms)}</span>
        </>
      }
    >
      {rows.length > 0 ? (
        <ol className="bc-rows">
          {rows.map((row) => {
            const known = roster.get(row.athleteId)
            return (
              <BroadcastRow
                key={row.athleteId}
                rank={row.rank}
                athlete={{ id: row.athleteId, name: row.name || known?.name || '—', photoUrl: known?.photoUrl ?? null }}
                km={row.progressKm}
                behindKm={row.behindKm}
                finished={row.finishedAt !== null}
                stale={row.stale}
                age={Number.isFinite(row.recordedAt) ? formatAge(now - row.recordedAt) : null}
              />
            )
          })}
        </ol>
      ) : (
        <p className="bc-panel__empty">No trackers on course yet</p>
      )}
    </BroadcastPanel>
  )
}

/**
 * The standings element: the spec names the board (race, gender, a hand-picked few), and the panel reads it every 10 s
 * while it shows, so it moves as the race does. A race the page can't find (not one of the event's, or one the server
 * won't rank) draws nothing, and so does a board still on its way.
 */
export function StandingsPanel({ spec, context, preview = false }: BroadcastElementProps<StandingsGraphic>) {
  const race = raceById(context.races, spec.routeId)
  const limit = rowsFor(spec)
  const { value: standings, missing } = useStandings(
    context.eventId,
    race ? { routeId: spec.routeId, gender: spec.gender, athleteIds: spec.athleteIds, limit } : null,
  )
  const now = useServerNow(Boolean(standings))
  if (!race || missing || !standings) return null
  return (
    <StandingsPanelView
      standings={standings}
      race={race}
      gender={spec.gender}
      ownTitle={spec.title}
      roster={context.roster}
      limit={context.maxRows ? Math.min(limit, context.maxRows) : limit}
      now={now}
      compact={context.compact}
      preview={preview}
    />
  )
}
